import React, { useState, useEffect } from 'react';
import { ledgerApi, sitesApi, entitiesApi, subCategoriesApi, exportApi } from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Download, FileSpreadsheet, Filter, Building2, ChevronDown, Paperclip } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { CATEGORIES, CATEGORY_LABELS, SUB_CAT_CATEGORIES, SUB_CAT_LABEL, SUB_CAT_PLURAL } from '@/constants/categories';

export default function LedgerPage() {
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ total_in: 0, total_out: 0, balance: 0 });
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [entities, setEntities] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [filters, setFilters] = useState({
    entity_id: '', site_id: '', category: '', sub_category: '', payment_mode: '', date_from: '', date_to: ''
  });
  const [includeBills, setIncludeBills] = useState(false);

  useEffect(() => {
    sitesApi.list().then(r => setSites(r.data)).catch(() => {});
    entitiesApi.list().then(r => setEntities(r.data)).catch(() => {});
    subCategoriesApi.list().then(r => setSubCategories(r.data)).catch(() => {});
  }, []);

  const fetchLedger = async () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const { data } = await ledgerApi.get(params);
      setEntries(data.entries);
      setTotals({ total_in: data.total_in, total_out: data.total_out, balance: data.balance });
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchLedger(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilters = () => fetchLedger();

  const buildExportParams = () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    return params;
  };

  const handleExport = (type, perspective) => {
    const params = { ...buildExportParams() };
    if (perspective) params.perspective = perspective;
    if (includeBills && (type === 'pdf' || type === 'pdfDrCr')) params.include_bills = 'true';
    const urlMap = { excel: exportApi.excel, pdf: exportApi.pdf, excelDrCr: exportApi.excelDrCr, pdfDrCr: exportApi.pdfDrCr };
    window.open(urlMap[type](params), '_blank');
  };

  // Filtered sub-categories for the filter dropdown
  const filterSubCats = subCategories.filter(sc => filters.category ? sc.parent_category === filters.category : true);
  const showSubCatFilter = SUB_CAT_CATEGORIES.includes(filters.category);

  return (
    <div className="space-y-6" data-testid="ledger-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Ledger</h1>
          <p className="text-sm text-muted-foreground mt-1">Date-wise entries with running balance</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <label className="flex items-center gap-1.5 cursor-pointer" data-testid="include-bills-toggle">
            <Switch checked={includeBills} onCheckedChange={setIncludeBills} className="scale-75" />
            <Paperclip className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground whitespace-nowrap">With Bills</span>
          </label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5" data-testid="ledger-export-excel">
                <FileSpreadsheet className="w-4 h-4" /> Excel <ChevronDown className="w-3 h-3 ml-0.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-xs">Standard</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleExport('excel')} data-testid="export-excel-ours">Our Perspective</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('excel', 'counterparty')} data-testid="export-excel-counter"><Building2 className="w-3.5 h-3.5 mr-1.5" /> Counterparty View</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs">Debit / Credit</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleExport('excelDrCr')} data-testid="export-excel-drcr-ours">Our Perspective (Dr/Cr)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('excelDrCr', 'counterparty')} data-testid="export-excel-drcr-counter"><Building2 className="w-3.5 h-3.5 mr-1.5" /> Counterparty View (Dr/Cr)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5" data-testid="ledger-export-pdf">
                <Download className="w-4 h-4" /> PDF <ChevronDown className="w-3 h-3 ml-0.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-xs">Standard</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="export-pdf-ours">Our Perspective</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf', 'counterparty')} data-testid="export-pdf-counter"><Building2 className="w-3.5 h-3.5 mr-1.5" /> Counterparty View</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs">Debit / Credit</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleExport('pdfDrCr')} data-testid="export-pdf-drcr-ours">Our Perspective (Dr/Cr)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdfDrCr', 'counterparty')} data-testid="export-pdf-drcr-counter"><Building2 className="w-3.5 h-3.5 mr-1.5" /> Counterparty View (Dr/Cr)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border p-4 space-y-3" data-testid="ledger-filters">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          <Filter className="w-3.5 h-3.5" /> Filters
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">Entity / Agency</Label>
            <Select value={filters.entity_id} onValueChange={v => setFilters(f => ({ ...f, entity_id: v === 'all' ? '' : v }))}>
              <SelectTrigger className="mt-1 h-8 text-xs" data-testid="ledger-filter-entity"><SelectValue placeholder="All Entities" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {entities.map(e => <SelectItem key={e._id} value={e._id}>{e.name} ({e.entity_type})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Site</Label>
            <Select value={filters.site_id} onValueChange={v => setFilters(f => ({ ...f, site_id: v === 'all' ? '' : v }))}>
              <SelectTrigger className="mt-1 h-8 text-xs" data-testid="ledger-filter-site"><SelectValue placeholder="All Sites" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sites</SelectItem>
                {sites.map(s => <SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Category</Label>
            <Select value={filters.category} onValueChange={v => setFilters(f => ({ ...f, category: v === 'all' ? '' : v, sub_category: '' }))}>
              <SelectTrigger className="mt-1 h-8 text-xs" data-testid="ledger-filter-category"><SelectValue placeholder="All Categories" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {showSubCatFilter && (
            <div>
              <Label className="text-xs text-muted-foreground">{SUB_CAT_LABEL[filters.category] || 'Sub-Type'}</Label>
              <Select value={filters.sub_category} onValueChange={v => setFilters(f => ({ ...f, sub_category: v === 'all' ? '' : v }))}>
                <SelectTrigger className="mt-1 h-8 text-xs" data-testid="ledger-filter-sub-category"><SelectValue placeholder={`All ${SUB_CAT_PLURAL[filters.category] || 'Items'}`} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All {SUB_CAT_PLURAL[filters.category] || 'Items'}</SelectItem>
                  {filterSubCats.map(sc => <SelectItem key={sc._id} value={sc.name}>{sc.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs text-muted-foreground">From Date</Label>
            <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} className="mt-1 h-8 text-xs" data-testid="ledger-filter-from" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Payment Mode</Label>
            <Select value={filters.payment_mode || 'all'} onValueChange={v => setFilters(f => ({ ...f, payment_mode: v === 'all' ? '' : v }))}>
              <SelectTrigger className="mt-1 h-8 text-xs" data-testid="ledger-filter-payment-mode"><SelectValue placeholder="All Modes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modes</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="bank">Bank</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">To Date</Label>
            <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} className="mt-1 h-8 text-xs" data-testid="ledger-filter-to" />
          </div>
          <div className="flex items-end">
            <Button size="sm" className="w-full h-8" onClick={handleApplyFilters} data-testid="ledger-apply-filters">Apply</Button>
          </div>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 p-3 sm:p-4" data-testid="ledger-total-in">
          <div className="text-[10px] sm:text-xs uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-semibold">Total Money In</div>
          <div className="font-mono text-lg sm:text-xl font-bold text-emerald-700 dark:text-emerald-400 mt-1">{'\u20B9'}{totals.total_in.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 p-3 sm:p-4" data-testid="ledger-total-out">
          <div className="text-[10px] sm:text-xs uppercase tracking-wider text-red-700 dark:text-red-400 font-semibold">Total Money Out</div>
          <div className="font-mono text-lg sm:text-xl font-bold text-red-700 dark:text-red-400 mt-1">{'\u20B9'}{totals.total_out.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-card border border-border p-3 sm:p-4" data-testid="ledger-balance">
          <div className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground font-semibold">Balance</div>
          <div className={`font-mono text-lg sm:text-xl font-bold mt-1 ${totals.balance >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>{'\u20B9'}{totals.balance.toLocaleString('en-IN')}</div>
        </div>
      </div>

      {/* Ledger Table */}
      <div className="bg-card border border-border overflow-x-auto -mx-2 sm:mx-0" data-testid="ledger-table">
        <table className="w-full min-w-[700px] text-sm">
          <thead>
            <tr className="border-b border-border bg-muted">
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Date</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Type</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Category</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Item</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Entity</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Mode</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Money In</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Money Out</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Balance</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-12 text-muted-foreground">Loading...</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-12 text-muted-foreground">No entries found. Adjust filters.</td></tr>
            ) : entries.map(entry => (
              <tr key={entry._id} className={`border-b border-border hover:bg-muted/50 transition-colors ${entry.dummy_account_name || (entry.sub_category || '').includes('Dummy Account') ? 'bg-amber-50/50 dark:bg-amber-950/10' : ''}`}>
                <td className="py-3 px-4 whitespace-nowrap">{entry.date}</td>
                <td className="py-3 px-4">
                  <span className={`text-xs font-medium ${entry.entry_type === 'income' ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                    {entry.entry_type === 'income' ? 'Money In' : 'Money Out'}
                  </span>
                  {(entry.dummy_account_name || (entry.sub_category || '').includes('Dummy Account')) && (
                    <span className="text-[10px] ml-1 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 rounded font-semibold">
                      {entry.dummy_account_name || 'Dummy'}
                    </span>
                  )}
                </td>
                <td className="py-3 px-4 capitalize max-w-[100px] truncate">{CATEGORY_LABELS[entry.category] || entry.category}</td>
                <td className="py-3 px-4 max-w-[100px] truncate">{entry.sub_category || '-'}</td>
                <td className="py-3 px-4 max-w-[100px] truncate">{entry.entity_name || '-'}</td>
                <td className="py-3 px-4 capitalize">{entry.payment_mode}</td>
                <td className="py-3 px-4 text-right font-mono text-emerald-700 dark:text-emerald-400">
                  {entry.entry_type === 'income' ? `\u20B9${entry.amount.toLocaleString('en-IN')}` : ''}
                </td>
                <td className="py-3 px-4 text-right font-mono text-red-700 dark:text-red-400">
                  {entry.entry_type === 'expense' ? `\u20B9${entry.amount.toLocaleString('en-IN')}` : ''}
                </td>
                <td className={`py-3 px-4 text-right font-mono font-medium ${entry.running_balance >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                  {'\u20B9'}{entry.running_balance.toLocaleString('en-IN')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
